import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useAttendanceContext } from '@/modules/Attendance/contexts/AttendanceContex'
import LoadingPage from '@/components/LoadingPage'
import CustomLayout from '@/components/CustomLayout'
import { redirectToCheckout } from '@/modules/Checkout/utils/redirectToCheckout'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { getTheme, toast, useMediaQuery } from '@conexasaude/conexa-hero'
import { RenderIf } from '@/components/RenderIf'
import screeningService from '../../../../../Screening/screening-services'
import DataConfirmationCard from '../DataConfirmationCard'
import DataEditidingForm from '../DataEditingForm'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { ConfirmContactWrapperType, UserConfirmDataFormType } from './types'
import { getUserConfirmDataSchema } from './schemas'
import { zodResolver } from '@hookform/resolvers/zod'
import { ISex } from '@/modules/Profile/profile-types'
import { getSexes } from '@/modules/Profile/profile-services'
import { verifyFreeAttendances } from '@/modules/Attendance/services'
import { setCache } from '@/utils/cache'
import { useScreeningData } from '@/modules/Screening/contexts/screeningContext'
import { formatToBRDate } from '@/utils/date'
import { Breadcrumb } from '@/components/Breadcrumb'
import { useNavigation } from '@/hooks/useNavigation'
import { getImmediateServiceBreadcrumbWithoutScreening } from './utils'
import { useRouteStore } from '@/modules/Auth/store/Route.store'
import { getBreadcrumbsFromHistory } from '../../../Scheduled/utils/buildBreadcrumbsFromHistory'

const ConfirmContactWrapper: FunctionComponent<ConfirmContactWrapperType> = ({
  hideBreadcrumb,
  attendanceType,
}) => {
  const navigate = useNavigation()
  const location = useLocation()
  const { user, clinic, updateUser } = useAuthContext()
  const { logAnalyticsEvent } = useFirebaseContext()
  const { setAttendance, attendance } = useAttendanceContext()
  const [isEditing, setIsEditing] = useState(false)
  const [sexOptions, setSexOptions] = useState<ISex[]>([])
  const { screeningData } = useScreeningData()
  const theme = getTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.grid.breakpoint.sm} )`)
  const attendanceTypeText =
    attendanceType === 'immediate' ? 'Imediata' : 'Agendada'

  const [updateLoading, setUpdateLoading] = useState(false)

  const { historyRoutes } = useRouteStore()

  const [contactDetails, setContactDetails] = useState<UserConfirmDataFormType>(
    {
      cellphone: user.parsedCellphone || '',
      birthdate: user.birthdate || '',
      email: user.email || '',
      cep: user.address.formattedCep || user.address.cep || '',
      streetName: user.address.streetName || '',
      number: user.address.number || '',
      complement: user.address.complement || '',
      neighborhood: user.address.neighborhood || '',
      city: user.address.city || '',
      state: user.address.state || '',
      sex: user.sex || '',
      emergencyContactFullName:
        user.contactsEmergency.emergencyContactFullName || '',
      emergencyContactCellphone:
        user.contactsEmergency.emergencyContactCellphone || '',
      relationship: user.contactsEmergency.relationship || '',
      emergencyContactId: user.contactsEmergency.emergencyContactId,
      daysPregnancy: user?.pregnantInformation?.daysPregnancy || '',
      weeksPregnancy: user?.pregnantInformation?.weeksPregnancy || '',
      lastMenstrualPeriodDate:
        user?.pregnantInformation?.lastMenstrualPeriodDate || '',
    },
  )

  const formMethods = useForm<UserConfirmDataFormType>({
    resolver: zodResolver(
      getUserConfirmDataSchema(
        clinic?.flag?.HabilitarObrigatoriedadeEndereco,
        clinic?.flag?.HabilitarObrigatoriedadeContatoEmergencia,
        clinic?.flag?.HabilitarFluxoDadosGestacionais,
        !!user?.pregnantInformation?.lastMenstrualPeriodDate,
      ),
    ),
    mode: 'onChange',
    defaultValues: contactDetails,
  })

  const { trigger, formState } = formMethods

  const shouldSkipConfirmation = useMemo(() => {
    if (!location) return false

    const urlSearchParams = new URLSearchParams(location.search)
    const hasSkipParam = !!urlSearchParams.get('skip')

    return hasSkipParam
  }, [location])

  const getBreadcrumbs = () => {
    if (location?.pathname === '/atendimento-imediato/atualizar-dados') {
      return getImmediateServiceBreadcrumbWithoutScreening(logAnalyticsEvent)
    }

    return getBreadcrumbsFromHistory({
      history: historyRoutes,
      logAnalyticsEvent,
      isMobile,
    })
  }

  const getFormOptionsFromServices = async () => {
    const sexes = await getSexes()
    setSexOptions(sexes)
  }

  const confirmContact = useCallback(async () => {
    setContactDetails({
      cep: contactDetails?.cep,
      streetName: contactDetails.streetName,
      number: contactDetails.number,
      complement: contactDetails.complement,
      neighborhood: contactDetails.neighborhood,
      city: contactDetails.city,
      sex: contactDetails.sex,
      cellphone: contactDetails.cellphone,
      birthdate: contactDetails?.birthdate,
      email: contactDetails.email,
      emergencyContactFullName: contactDetails.emergencyContactFullName,
      emergencyContactCellphone: contactDetails.emergencyContactCellphone,
      relationship: contactDetails.relationship,
      state: contactDetails.state,
      emergencyContactId: contactDetails.emergencyContactId,
      daysPregnancy: user?.pregnantInformation?.daysPregnancy,
      weeksPregnancy: user?.pregnantInformation?.weeksPregnancy,
      lastMenstrualPeriodDate: formatToBRDate(
        user?.pregnantInformation?.lastMenstrualPeriodDate,
      ),
    })
    setAttendance(att => {
      att.patientContact = contactDetails
      return att
    })
    // clinica hibrida
    if (attendanceType === 'immediate') {
      const hasFreeAttendances = await verifyFreeAttendances({
        professionalType: screeningData?.professionalType,
        attendanceType: 'IMEDIATO',
        date: attendance?.date,
      })
      setCache('has-free-attendance', hasFreeAttendances)
      const hasCheckoutEnabled = clinic?.flag?.PermiteConsultaParticular
      if (
        hasCheckoutEnabled &&
        hasFreeAttendances === 'limitReached' &&
        !clinic?.flag?.ClinicaUnificadaPV
      ) {
        toast({
          message:
            'Realize o pagamento da consulta. Você atingiu  o limite de atendimentos gratuitos. Finalize esse agendamento, realizando o pagamento.',
          variant: 'warning',
          autoCloseDuration: 5000,
          position: 'top-right',
        })
      }
    }
    redirectToCheckout({
      attendanceType: attendanceType,
      push: navigate,
      hasPayment: clinic?.flag?.PermiteConsultaParticular,
      hasMultipleCards: clinic?.flag?.HabilitarMultiplosCartoesConvenio,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const confirmContactAndSendAnalyticsEvent = useCallback(async () => {
    logAnalyticsEvent(`Pac_${attendanceTypeText}_ConfirmarEContinuar`)
    confirmContact()
  }, [attendanceTypeText, confirmContact, logAnalyticsEvent])

  const editContacts = () => {
    setIsEditing(true)
    logAnalyticsEvent(`Pac_${attendanceTypeText}_EditarDados`)
  }

  const updateUserData = async (
    data: UserConfirmDataFormType,
  ): Promise<void> => {
    setUpdateLoading(true)
    logAnalyticsEvent(`Pac_${attendanceTypeText}_Atualizar`)
    try {
      await screeningService.updateUserDataInformation(
        data,
        user?.contactsEmergency?.emergencyContactId,
      )
      const emergencyContactIdUser =
        await screeningService.getEmergencyContactId()
      updateUser(user => {
        user.email = data.email
        user.cellphone = data.cellphone
        user.parsedCellphone = data.cellphone
        user.birthdate = data?.birthdate
        user.sex = data.sex
        user.address.formattedCep = data.cep
        user.address.streetName = data.streetName
        user.address.number = data.number
        user.address.complement = data.complement
        user.address.neighborhood = data.neighborhood
        user.address.city = data.city
        user.address.state = data.state
        user.contactsEmergency.emergencyContactFullName =
          data.emergencyContactFullName
        user.contactsEmergency.emergencyContactCellphone =
          data.emergencyContactCellphone
        user.contactsEmergency.relationship = data.relationship
        user.contactsEmergency.emergencyContactId =
          emergencyContactIdUser || null
        user.pregnantInformation.daysPregnancy = data?.daysPregnancy
        user.pregnantInformation.weeksPregnancy = data?.weeksPregnancy
        user.pregnantInformation.lastMenstrualPeriodDate = formatToBRDate(
          data?.lastMenstrualPeriodDate,
        )

        return user
      })
      toast({
        message: 'Alterações salvas com sucesso!',
        variant: 'success',
        autoCloseDuration: 5000,
        position: 'top-right',
      })
      setIsEditing(false)
    } catch (err) {
      const errorMessage =
        err?.response?.data?.object?.msg ||
        err?.response?.data?.msg ||
        err?.message

      toast({
        message: errorMessage,
        variant: 'error',
        autoCloseDuration: 5000,
        position: 'top-right',
      })
    } finally {
      setUpdateLoading(false)
    }
  }

  const onSubmit: SubmitHandler<UserConfirmDataFormType> = data => {
    try {
      if (isEditing) {
        updateUserData(data)
      } else {
        confirmContactAndSendAnalyticsEvent()
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    getFormOptionsFromServices()
  }, [])

  useEffect(() => {
    if (!attendance?.speciality) navigate('/')
  }, [navigate, attendance])

  useEffect(() => {
    trigger(['cellphone', 'sex', 'birthdate', 'email'])

    if (clinic?.flag?.HabilitarObrigatoriedadeEndereco) {
      trigger(['cep', 'city', 'neighborhood', 'number', 'streetName', 'state'])
    }
    if (clinic?.flag?.HabilitarObrigatoriedadeContatoEmergencia) {
      trigger([
        'emergencyContactCellphone',
        'emergencyContactFullName',
        'relationship',
      ])
    }
    if (clinic?.flag?.HabilitarFluxoDadosGestacionais) {
      trigger(['weeksPregnancy', 'daysPregnancy', 'lastMenstrualPeriodDate'])
    }

    if (Object?.values(formState?.errors)?.length) {
      setIsEditing(true)
      toast({
        message: 'Verifique os campos com erro',
        variant: 'error',
        autoCloseDuration: 5000,
        position: 'top-right',
      })
    }
  }, [
    clinic?.flag?.HabilitarFluxoDadosGestacionais,
    clinic?.flag?.HabilitarObrigatoriedadeContatoEmergencia,
    clinic?.flag?.HabilitarObrigatoriedadeEndereco,
    formState?.errors,
    trigger,
  ])

  useEffect(() => {
    if (shouldSkipConfirmation) {
      confirmContact()
    }
  }, [confirmContact, location, shouldSkipConfirmation])

  if (shouldSkipConfirmation) return <LoadingPage withoutNav />

  return (
    <CustomLayout
      hasPrimaryAction
      primaryActionLabel={isEditing ? 'Atualizar' : 'Confirmar e continuar'}
      primaryAction={formMethods.handleSubmit(onSubmit)}
      primaryActionProps={{
        disabled: isEditing && updateLoading,
      }}
      hasLayoutMenu
      layoutMenu={
        !hideBreadcrumb ? <Breadcrumb items={getBreadcrumbs()} /> : null
      }
    >
      <FormProvider {...formMethods}>
        <form>
          <RenderIf condition={!isEditing}>
            <DataConfirmationCard
              handleClick={editContacts}
              sexes={sexOptions}
            />
          </RenderIf>
          <RenderIf condition={isEditing}>
            <DataEditidingForm
              sexes={sexOptions}
              hidePregnancyForm={!clinic?.flag?.HabilitarFluxoDadosGestacionais}
            />
          </RenderIf>
        </form>
      </FormProvider>
    </CustomLayout>
  )
}

export default ConfirmContactWrapper
